import { MouseEventHandler, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import styles from './styles.module.css'
import PrimaryButton from '../../../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../../../global/components/buttons/secondary-button/SecondaryButton'
import TechnicianModalContent from './TechnicianModalContent'
import CircularProgress from '@mui/material/CircularProgress'
import {
  createTech,
  createTechnicianWeekCalendars,
  editTech,
  editTechnicianWeekCalendar,
  getPicturesByIds,
  getTechById,
  setTechToLocation,
  initializeTechnicianSchedule,
  determineEffectOfTechnicianUpdate,
} from '../../api'
import { fetchData } from '../../../../../global/utils/fetch'
import ToastNotification, {
  IAlertStatusProps,
} from '../../../../../global/components/toast-alert/ToastNotification'
import {
  ITechnician,
  ITechnicianPartial,
} from '../../../../../../../app/entities/Technician'
import {
  IWeekCalendar,
  IWeekCalendarPartial,
} from '../../../../../../../app/entities/WeekCalendar'
import {
  initialWeekCalendarFormValues,
  parseTimeStringToMinutes,
} from '../utils'
import { ILocation } from '../../../../../../../app/entities/Location'
import { DayAnalysisResult } from '../../../../../../../app/types/technician-impact-analysis'

interface ITechnicianModalProps {
  isOpen: boolean
  handleClosed: MouseEventHandler
  technicianObjectId?: string
}

function doChangesAffectDayPlans(
  updatedTechnicianData: ITechnicianPartial,
  originalTechnician: ITechnicianPartial | null,
  updatedLocationObjectId: string,
  originalLocation: ILocation | null,
) {
  const inactiveChanged =
    originalTechnician &&
    updatedTechnicianData.inactive !== originalTechnician.inactive
  const acceptsDeliveriesChanged =
    originalTechnician &&
    updatedTechnicianData.acceptsDeliveries !==
      originalTechnician.acceptsDeliveries
  const acceptsInstallationsChanged =
    originalTechnician &&
    updatedTechnicianData.acceptsInstallations !==
      originalTechnician.acceptsInstallations
  const locationChanged =
    originalLocation && updatedLocationObjectId !== originalLocation.objectId

  return (
    inactiveChanged ||
    acceptsDeliveriesChanged ||
    acceptsInstallationsChanged ||
    locationChanged
  )
}

export function TechnicianModal({
  isOpen,
  handleClosed,
  technicianObjectId,
}: ITechnicianModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [alertStatus, setAlertStatus] = useState<IAlertStatusProps>({
    isOpen: false,
    message: '',
    severity: 'error',
  })
  const [technicianData, setTechnicianData] = useState<ITechnicianPartial>({})
  const [originalTechnician, setOriginalTechnician] =
    useState<ITechnicianPartial | null>(null)

  const [weekCalendars, setWeekCalendars] = useState<IWeekCalendarPartial[]>(
    initialWeekCalendarFormValues,
  )
  const [location, setLocation] = useState<ILocation | null>(null)
  const [originalLocation, setOriginalLocation] = useState<ILocation | null>(
    null,
  )
  const [isDefaultLunch, setIsDefaultLunch] = useState(true)
  const [isDefaultWorkDay, setIsDefaultWorkDay] = useState(true)
  const [isSaveDisabled, setIsSaveDisabled] = useState(true)

  function handleClose(e: any) {
    setTechnicianData({})
    setWeekCalendars(initialWeekCalendarFormValues)
    setLocation(null)
    setOriginalLocation(null)
    setIsLoading(false)
    handleClosed(e)
  }

  async function handleSaveTech(e: any) {
    try {
      setIsLoading(true)
      let dataToSave = { ...technicianData }
      if (isDefaultWorkDay && location) {
        dataToSave = {
          ...dataToSave,
          workDayFrom: location.workDayFrom,
          workDayTo: location.workDayTo,
        }
      }
      if (isDefaultLunch && location) {
        dataToSave = {
          ...dataToSave,
          lunchDuration: location.lunchDuration,
        }
      }

      if (
        doChangesAffectDayPlans(
          dataToSave,
          originalTechnician,
          location?.objectId ?? '',
          originalLocation,
        )
      ) {
        const newTechnicianMode: Pick<
          ITechnician,
          'inactive' | 'acceptsDeliveries' | 'acceptsInstallations'
        > = {
          inactive: dataToSave.inactive ?? false,
          acceptsDeliveries: dataToSave.acceptsDeliveries ?? true,
          acceptsInstallations: dataToSave.acceptsInstallations ?? true,
        }
        const newLocationObjectId =
          originalLocation?.objectId === location?.objectId
            ? undefined
            : location?.objectId

        const dayAnalysisResults = await fetchData<DayAnalysisResult[]>(
          determineEffectOfTechnicianUpdate(
            technicianObjectId ?? '',
            false, //bumpOrdersToPendingRescheduling
            newTechnicianMode,
            newLocationObjectId,
          ),
        )

        const errors: string[] = []
        dayAnalysisResults.forEach((result) => {
          if (result.error) {
            errors.push(`Date: ${result.date}, Error: ${result.error}`)
          }
        })

        if (errors.length > 0) {
          alert(
            `Unable to determine impact of changes due to the following errors:\n${errors.join(
              '\n',
            )}\n\nPlease fix these issues and try again.`,
          )
          return
        }

        const affectedWorkOrdersIds = dayAnalysisResults.flatMap((result) => {
          const droppedOrders = result.droppedOrders ?? []
          const workOrderIds = droppedOrders.map((order) => order.workOrderId)
          return workOrderIds
        })

        if (affectedWorkOrdersIds.length > 0) {
          if (
            !window.confirm(
              [
                'This change will bump the following orders to pending rescheduling:',
                `${affectedWorkOrdersIds.join(',\n')}`,
                'Are you sure you want to continue?',
              ].join('\n'),
            )
          ) {
            return
          }
        }
      }
      // Remove daySettings from payload - backend doesn't use it and it wastes bandwidth
      const { daySettings, ...technicianDataWithoutDaySettings } = dataToSave
      await Promise.all([
        fetchData(editTech(technicianDataWithoutDaySettings)), //this will updates orders to pending rescheduling as needed
        fetchData<IWeekCalendar>(
          editTechnicianWeekCalendar(weekCalendars?.[0], technicianObjectId),
        ),
        fetchData<IWeekCalendar>(
          editTechnicianWeekCalendar(weekCalendars?.[1], technicianObjectId),
        ),
      ])
      await fetchData(
        setTechToLocation(location?.objectId ?? '', technicianObjectId ?? ''), //this will updates orders to pending rescheduling as needed
      )
      handleClose(e)
    } catch (error) {
      alert(
        `Error updating Technician: ${JSON.stringify(
          error,
          Object.getOwnPropertyNames(error),
        )}`,
      )
    } finally {
      setIsLoading(false)
    }
  }

  async function handleCreateTech(e: any) {
    try {
      setIsLoading(true)
      let dataToSave = { ...technicianData }
      if (isDefaultWorkDay && location) {
        dataToSave = {
          ...dataToSave,
          workDayFrom: location.workDayFrom,
          workDayTo: location.workDayTo,
        }
      }
      if (isDefaultLunch && location) {
        dataToSave = {
          ...dataToSave,
          lunchDuration: location.lunchDuration,
        }
      }
      const newTechnician = await fetchData<ITechnician>(createTech(dataToSave))

      // Determine if location is in DaySettings-only mode
      const isDaySettingsOnlyMode = location?.daySettingsOnlyMode === true

      // Create WeekCalendars for all technicians (backwards compatibility)
      await fetchData<void>(
        createTechnicianWeekCalendars(weekCalendars, newTechnician.objectId),
      )

      // Set location
      await fetchData<void>(
        setTechToLocation(location?.objectId ?? '', newTechnician.objectId),
      )

      if (isDaySettingsOnlyMode) {
        // For DaySettings-only locations: also initialize schedule
        // This allows technician to work in new schedule system
        await fetchData<void>(
          initializeTechnicianSchedule(newTechnician.objectId),
        )
      }

      handleClose(e)
    } catch (error) {
      alert(
        `Error updating Technician: ${JSON.stringify(
          error,
          Object.getOwnPropertyNames(error),
        )}`,
      )
    } finally {
      setIsLoading(false)
    }
  }

  async function fetchTechnicianData(technicianObjectId: string) {
    setIsLoading(true)
    const [newTechnician, technicianProfilePictures] = await Promise.all([
      fetchData<ITechnician>(getTechById(technicianObjectId)),
      fetchData<
        {
          technicianObjectId: string
          profilePictureURL?: string
          orderTrackerPictureURL?: string
        }[]
      >(getPicturesByIds([technicianObjectId])),
    ])
    setTechnicianData({
      ...newTechnician,
      profilePicture: technicianProfilePictures[0].profilePictureURL,
    })
    setOriginalTechnician({ ...newTechnician })
    const isDefault =
      newTechnician.location &&
      newTechnician.workDayFrom === newTechnician.location.workDayFrom &&
      newTechnician.workDayTo === newTechnician.location.workDayTo
    setIsDefaultWorkDay(!!isDefault)
    const isDefaultLunchValue =
      newTechnician.location &&
      (newTechnician.lunchDuration === undefined ||
        newTechnician.lunchDuration === null ||
        newTechnician.lunchDuration === newTechnician.location.lunchDuration)
    setIsDefaultLunch(!!isDefaultLunchValue)
    setWeekCalendars(newTechnician.weekCalendars)
    setLocation(newTechnician.location ?? null)
    setOriginalLocation(newTechnician.location ?? null)
    setIsLoading(false)
  }

  useEffect(() => {
    if (!technicianObjectId) return
    fetchTechnicianData(technicianObjectId)
  }, [technicianObjectId])

  useEffect(() => {
    const useDefaultWorkDay =
      !technicianData.workDayTo || !technicianData.workDayFrom
    const isValidTechnicianWorkTime = useDefaultWorkDay
      ? true
      : parseTimeStringToMinutes(technicianData.workDayTo) >
        parseTimeStringToMinutes(technicianData.workDayFrom)

    const isTechnicianFormInfoInvalid =
      !technicianData.name ||
      !technicianData.email ||
      !location?.objectId ||
      !technicianData.employeeId ||
      !technicianData.samsaraId ||
      !isValidTechnicianWorkTime

    setIsSaveDisabled(isTechnicianFormInfoInvalid)
  }, [technicianData, location])

  function toggleAlert(open: boolean) {
    setAlertStatus({ ...alertStatus, isOpen: open })
  }

  return (
    <div>
      <Modal
        open={isOpen}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box className={styles.techModal}>
          <>
            <div className={styles.locationModalHeader}>
              <ToastNotification
                severity={alertStatus.severity}
                open={alertStatus.isOpen}
                message={alertStatus.message}
                handleClose={() => toggleAlert(false)}
              />
              <div className={`${styles.locationModalPageTitle} font--bold`}>
                {technicianObjectId
                  ? 'Edit Technician'
                  : 'Create New Technician'}
              </div>
              <div className={styles.locationModalHeaderButtons}>
                <SecondaryButton
                  buttonName='Cancel'
                  onClick={handleClose}
                />
                {technicianObjectId ? (
                  <PrimaryButton
                    disabled={isLoading || isSaveDisabled}
                    buttonName='Save Changes'
                    onClick={handleSaveTech}
                  />
                ) : (
                  <PrimaryButton
                    buttonName='Create Tech'
                    disabled={isLoading || isSaveDisabled}
                    onClick={handleCreateTech}
                  />
                )}
              </div>
            </div>
            {isLoading && (
              <div className={styles.progressContainer}>
                <CircularProgress />
              </div>
            )}
            {!isLoading && (
              <TechnicianModalContent
                technicianData={technicianData}
                setTechnicianData={setTechnicianData}
                isDefaultLunch={isDefaultLunch}
                setIsDefaultLunch={setIsDefaultLunch}
                isDefaultWorkDay={isDefaultWorkDay}
                setIsDefaultWorkDay={setIsDefaultWorkDay}
                technicianObjectId={technicianObjectId}
                weekCalendars={weekCalendars}
                setWeekCalendars={setWeekCalendars}
                location={location}
                setLocation={setLocation}
                originalLocation={originalLocation}
                refetchTechnicianData={
                  technicianObjectId
                    ? () => fetchTechnicianData(technicianObjectId)
                    : () => {}
                }
              />
            )}
          </>
        </Box>
      </Modal>
    </div>
  )
}
